'use client';

import { Loader2Icon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState, useTransition } from 'react';

import type { HomeIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { globalStore } from '@/app/globalStore';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { cn } from '@/lib/utils';

/** Navigation toggle button component */
export const TitleBarNavLinkButton = observer(({
  pathname,
  icon: Icon,
  children,
  targetPath,
  tooltip,
  disabled,
  className
}: {
  pathname: string;
  icon?: typeof HomeIcon;
  children?: ReactNode;
  targetPath: string;
  tooltip: string;
  disabled?: boolean;
  className?: string;
}) => {
  const { safeNavigate, isNavigating } = useSafeNavigation();
  const [isPending, startTransition] = useTransition();
  const [isPressed, setIsPressed] = useState(false);
  const isActive = pathname.startsWith(targetPath);

  // Override disabled if globalStore.loadingOverlay is true
  const isDisabled = globalStore.loadingOverlay
    ? true
    : Boolean(disabled) || isPending || isNavigating;

  const handleNavigation = () => {
    if (isDisabled) return;
    // Already on this route — avoid redundant navigation and toggle churn
    if (isActive) return;

    setIsPressed(true);
    startTransition(() => {
      safeNavigate(targetPath);
      // Reset pressed state after a short delay to ensure visual feedback
      setTimeout(() => setIsPressed(false), 150);
    });
  };

  return (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild>
        <span>
          <Toggle
            variant="titleBar"
            size="titleBar"
            pressed={isActive || isPressed}
            onPressedChange={handleNavigation}
            className={cn('titlebarControls', className)}
            disabled={isDisabled}
          >
            {isPending || isNavigating
              ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
              : <>{Icon && <Icon className="h-3.5 w-3.5" />}</>}
            {children}
          </Toggle>
        </span>
      </TooltipTrigger>
      <TooltipContent>{isPending || isNavigating ? 'Loading...' : tooltip}</TooltipContent>
    </Tooltip>
  );
});